import { useNavigate } from 'react-router-dom'
import { useTheme } from '../contexts/ThemeContext'
import { 
  LayoutGrid, 
  Users, 
  Zap, 
  Shield, 
  Globe, 
  Smartphone,
  ChevronRight,
  Check,
  Calendar,
  CheckSquare,
  StickyNote,
  Cloud,
  Music,
  Tv,
  Image,
  Bookmark,
  Mail,
  Rss,
  TrendingUp,
  Wind,
  Search,
  MessageSquare,
  Palette,
  Code,
  Share2,
  Lock,
  Star,
  ArrowRight,
  Sparkles
} from 'lucide-react'
import { useState } from 'react'

export default function Home() {
  const navigate = useNavigate()
  const { currentTheme } = useTheme()
  const logoSrc = currentTheme?.is_dark ? '/DashitLogo-white.png' : '/DashitLogo.png'
  const [activePlan, setActivePlan] = useState('pro')

  const features = [
    {
      icon: <LayoutGrid className="w-6 h-6" />,
      title: 'Drag & Drop Interface',
      description: 'Intuitive drag-and-drop builder lets you arrange your dashboard exactly how you want it'
    },
    {
      icon: <Users className="w-6 h-6" />,
      title: 'Multi-User Dashboards',
      description: 'Share dashboards with family, friends, or team members with granular permission controls'
    },
    {
      icon: <Palette className="w-6 h-6" />,
      title: 'Custom Themes',
      description: 'Choose from preset themes or create your own with dark mode, colors, and custom styling'
    },
    {
      icon: <Zap className="w-6 h-6" />,
      title: 'Lightning Fast',
      description: 'Built with modern React and optimized for speed—instant loading and smooth interactions'
    },
    {
      icon: <Shield className="w-6 h-6" />,
      title: 'Secure & Private',
      description: 'Enterprise-grade security with encrypted data and OAuth integration for calendar sync'
    },
    {
      icon: <Code className="w-6 h-6" />,
      title: 'Developer API',
      description: 'Build and publish custom widgets to the app store with our comprehensive API'
    },
    {
      icon: <Globe className="w-6 h-6" />,
      title: 'Cloud-First',
      description: 'Access your dashboard from anywhere—your data syncs across all your devices'
    },
    {
      icon: <Smartphone className="w-6 h-6" />,
      title: 'Mobile Responsive',
      description: 'Fully optimized experience on mobile, tablet, and desktop with touch support'
    },
    {
      icon: <MessageSquare className="w-6 h-6" />,
      title: 'Voice Commands',
      description: 'Use voice chat to add tasks, create events, and control your dashboard hands-free'
    }
  ]

  const widgets = [
    { icon: <Search />, name: 'Google Search', description: 'Quick web search' },
    { icon: <Calendar />, name: 'Calendar', description: 'Sync with Google & Outlook' },
    { icon: <CheckSquare />, name: 'Tasks', description: 'Multi-level to-do lists' },
    { icon: <StickyNote />, name: 'Notes', description: 'Quick note taking' },
    { icon: <Cloud />, name: 'Weather', description: '5-day forecast by ZIP' },
    { icon: <Wind />, name: 'Wind & Tide', description: 'Marine conditions' },
    { icon: <TrendingUp />, name: 'Sports Scores', description: 'Live game updates' },
    { icon: <Rss />, name: 'RSS Feeds', description: 'Follow your feeds' },
    { icon: <Bookmark />, name: 'Bookmarks', description: 'Organized links' },
    { icon: <Mail />, name: 'Email', description: 'Inbox integration' },
    { icon: <Music />, name: 'Spotify', description: 'Music streaming' },
    { icon: <Tv />, name: 'TV Series', description: 'Track shows' },
    { icon: <Image />, name: 'Photo Albums', description: 'Photo galleries' },
  ]

  const plans = [
    {
      id: 'free',
      name: 'Free',
      price: '$0',
      period: 'forever',
      description: 'Perfect for personal use',
      features: [
        '1 Dashboard',
        '2 Tabs',
        'Up to 10 widgets',
        'Basic widgets',
        'Mobile access',
        'Community support'
      ],
      cta: 'Get Started',
      popular: false
    },
    {
      id: 'pro',
      name: 'Pro',
      price: '$9',
      period: 'per month',
      description: 'For power users and families',
      features: [
        'Unlimited dashboards',
        'Unlimited tabs',
        'Unlimited widgets',
        'All premium widgets',
        'Custom themes',
        'Calendar sync (Google/Outlook)',
        'Share with up to 5 users',
        'Priority support',
        'Voice commands'
      ],
      cta: 'Sign Up Free',
      popular: true
    },
    {
      id: 'enterprise',
      name: 'Enterprise',
      price: '$29',
      period: 'per month',
      description: 'For teams and organizations',
      features: [
        'Everything in Pro',
        'Unlimited user sharing',
        'Team collaboration',
        'Custom branding',
        'SSO integration',
        'Advanced analytics',
        'API access',
        'Dedicated support',
        'SLA guarantee'
      ],
      cta: 'Contact Sales',
      popular: false
    }
  ]

  const testimonials = [
    {
      name: 'Sarah Chen',
      role: 'Product Manager',
      avatar: '👩‍💼',
      quote: 'Dashit.io has completely transformed how I manage my work and personal life. Everything I need is right at my fingertips!'
    },
    {
      name: 'Mike Johnson',
      role: 'Small Business Owner',
      avatar: '👨‍💻',
      quote: 'The ability to share dashboards with my team has been a game-changer. We\'re more organized than ever.'
    },
    {
      name: 'Emily Rodriguez',
      role: 'Busy Parent',
      avatar: '👩‍👧‍👦',
      quote: 'Finally, one place for all our family activities, schedules, and tasks. My whole family loves it!'
    }
  ]

  return (
    <div className="min-h-screen bg-white">
      {/* Header/Nav */}
      <nav className="sticky top-0 z-50 bg-white/80 backdrop-blur-lg border-b border-gray-200">
        <div className="container mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center">
            <img
              src={logoSrc}
              alt="Dashit"
              className="h-10 w-auto"
              onError={(e) => {
                e.currentTarget.style.display = 'none';
                const text = document.createElement('span');
                text.textContent = 'Dashit';
                text.className = 'text-2xl font-bold text-primary-500';
                e.currentTarget.parentElement?.appendChild(text);
              }}
            />
          </div>
          <div className="flex items-center gap-4">
            <a href="#features" className="px-4 py-2 text-gray-700 hover:text-primary-600 transition hidden md:block">
              Features
            </a>
            <a href="#pricing" className="px-4 py-2 text-gray-700 hover:text-primary-600 transition hidden md:block">
              Pricing
            </a>
            <a href="#widgets" className="px-4 py-2 text-gray-700 hover:text-primary-600 transition hidden md:block">
              Widgets
            </a>
            <button
              onClick={() => navigate('/login')}
              className="px-5 py-2 text-gray-700 hover:text-primary-600 transition font-medium"
            >
              Sign In
            </button>
            <button
              onClick={() => navigate('/register')}
              className="px-6 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 hover:shadow-lg transition font-semibold"
            >
              Get Started Free
            </button>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative overflow-hidden bg-gradient-to-br from-primary-50 via-white to-primary-100">
        <div className="absolute inset-0 bg-grid-pattern opacity-[0.02]"></div>
        <div className="container mx-auto px-6 py-20 md:py-32 relative">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-primary-100 text-primary-700 rounded-full text-sm font-medium mb-8">
              <Sparkles className="w-4 h-4" />
              Now with AI Voice Commands & Custom Themes
            </div>
            
            <h1 className="text-5xl md:text-7xl font-bold mb-6 leading-tight">
              <span className="text-gray-900">
                Your Life,
              </span>
              <br />
              <span className="text-primary-500">
                Beautifully Organized
              </span>
            </h1>
            
            <p className="text-xl md:text-2xl text-gray-600 mb-10 max-w-3xl mx-auto leading-relaxed">
              The ultimate dashboard for personal life, family, and work. 
              Customizable, collaborative, and cloud-first.
            </p>
            
            <div className="flex items-center justify-center">
              <button
                onClick={() => navigate('/register')}
                className="px-10 py-4 bg-primary-500 text-white rounded-xl hover:bg-primary-600 hover:shadow-xl transition font-bold text-lg flex items-center gap-2 group"
              >
                Sign Up Free
                <ChevronRight className="w-5 h-5 group-hover:translate-x-1 transition" />
              </button>
            </div>
            
            <p className="text-gray-500 mt-6 text-sm">
              No credit card required • Free forever
            </p>
          </div>

          {/* Hero Image/Dashboard Preview */}
          <div className="max-w-6xl mx-auto mt-16">
            <div className="relative rounded-2xl overflow-hidden shadow-2xl border-4 border-white">
              <div className="bg-primary-500 p-8">
                <div className="bg-white rounded-xl shadow-lg p-6">
                  <div className="flex items-center gap-3 mb-6">
                    <div className="flex gap-2">
                      <div className="w-3 h-3 rounded-full bg-red-400"></div>
                      <div className="w-3 h-3 rounded-full bg-yellow-400"></div>
                      <div className="w-3 h-3 rounded-full bg-green-400"></div>
                    </div>
                    <div className="flex-1 bg-gray-100 rounded-lg px-4 py-2 text-sm text-gray-500">
                      My Dashboard
                    </div>
                  </div>
                  <div className="grid grid-cols-3 gap-4">
                    {[1, 2, 3, 4, 5, 6].map((i) => (
                      <div key={i} className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-lg p-4 h-32 animate-pulse"></div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-24 bg-gradient-to-b from-white to-gray-50">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-4 bg-gradient-to-r from-gray-900 to-gray-700 bg-clip-text text-transparent">
              Everything You Need to Stay Organized
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Powerful features designed to simplify your life and boost productivity
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {features.map((feature, index) => (
              <div
                key={index}
                className="bg-white rounded-2xl p-6 hover:shadow-xl transition border border-gray-100 group"
              >
                <div className="w-12 h-12 bg-primary-500 rounded-xl flex items-center justify-center text-white mb-4 group-hover:scale-110 transition">
                  {feature.icon}
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">
                  {feature.title}
                </h3>
                <p className="text-gray-600">
                  {feature.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Widgets Showcase */}
      <section id="widgets" className="py-24 bg-white">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-4 bg-gradient-to-r from-gray-900 to-gray-700 bg-clip-text text-transparent">
              Powerful Widgets for Everything
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Choose from our collection of built-in widgets, or create your own with our API
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6 max-w-6xl mx-auto mb-12">
            {widgets.map((widget, index) => (
              <div
                key={index}
                className="bg-gradient-to-br from-gray-50 to-white rounded-xl p-6 hover:shadow-lg transition border border-gray-200 group cursor-pointer"
              >
                <div className="w-12 h-12 bg-primary-100 rounded-lg flex items-center justify-center text-primary-600 mb-3 group-hover:bg-primary-600 group-hover:text-white transition">
                  {widget.icon}
                </div>
                <h3 className="font-semibold text-gray-900 mb-1">{widget.name}</h3>
                <p className="text-sm text-gray-500">{widget.description}</p>
              </div>
            ))}
            <div className="bg-gradient-to-br from-primary-50 to-primary-100 rounded-xl p-6 flex flex-col items-center justify-center text-center border-2 border-dashed border-primary-300">
              <Plus className="w-12 h-12 text-primary-600 mb-3" />
              <h3 className="font-semibold text-gray-900 mb-1">Build Your Own</h3>
              <p className="text-sm text-gray-500">Use our API</p>
            </div>
          </div>

          <div className="text-center">
            <button
              onClick={() => navigate('/register')}
              className="px-8 py-3 bg-primary-500 text-white rounded-xl hover:bg-primary-600 transition font-semibold inline-flex items-center gap-2"
            >
              Explore All Widgets
              <ArrowRight className="w-5 h-5" />
            </button>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="py-24 bg-gradient-to-b from-gray-50 to-white">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-4 bg-gradient-to-r from-gray-900 to-gray-700 bg-clip-text text-transparent">
              Simple, Transparent Pricing
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Choose the plan that's right for you. Start free, upgrade anytime.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {plans.map((plan) => (
              <div
                key={plan.id}
                className={`relative bg-white rounded-2xl p-8 border-2 transition hover:shadow-xl ${
                  plan.popular
                    ? 'border-primary-500 shadow-lg scale-105'
                    : 'border-gray-200'
                }`}
              >
                {plan.popular && (
                  <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                    <span className="bg-primary-500 text-white px-4 py-1 rounded-full text-sm font-semibold">
                      Most Popular
                    </span>
                  </div>
                )}

                <div className="text-center mb-6">
                  <h3 className="text-2xl font-bold text-gray-900 mb-2">{plan.name}</h3>
                  <p className="text-gray-500 mb-4">{plan.description}</p>
                  <div className="flex items-baseline justify-center gap-1">
                    <span className="text-5xl font-bold text-gray-900">{plan.price}</span>
                    <span className="text-gray-500">/{plan.period}</span>
                  </div>
                </div>

                <ul className="space-y-3 mb-8">
                  {plan.features.map((feature, index) => (
                    <li key={index} className="flex items-start gap-3">
                      <Check className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                      <span className="text-gray-600">{feature}</span>
                    </li>
                  ))}
                </ul>

                <button
                  onClick={() => navigate('/register')}
                  className={`w-full py-3 rounded-xl font-semibold transition ${
                    plan.popular
                      ? 'bg-primary-500 text-white hover:bg-primary-600 hover:shadow-lg'
                      : 'bg-gray-100 text-gray-900 hover:bg-gray-200'
                  }`}
                >
                  {plan.cta}
                </button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-24 bg-white">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-4 bg-gradient-to-r from-gray-900 to-gray-700 bg-clip-text text-transparent">
              They Love Us!
            </h2>
            <p className="text-xl text-gray-600">
              See what our users have to say
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {testimonials.map((testimonial, index) => (
              <div
                key={index}
                className="bg-gradient-to-br from-gray-50 to-white rounded-2xl p-8 border border-gray-200 hover:shadow-lg transition"
              >
                <div className="flex items-center gap-4 mb-4">
                  <div className="text-4xl">{testimonial.avatar}</div>
                  <div>
                    <div className="font-semibold text-gray-900">{testimonial.name}</div>
                    <div className="text-sm text-gray-500">{testimonial.role}</div>
                  </div>
                </div>
                <div className="flex gap-1 mb-3">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <Star key={star} className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                  ))}
                </div>
                <p className="text-gray-600 italic">"{testimonial.quote}"</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-primary-500 relative overflow-hidden">
        <div className="absolute inset-0 bg-grid-pattern opacity-10"></div>
        <div className="container mx-auto px-6 relative">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
              Ready to Transform Your Life?
            </h2>
            <p className="text-xl text-primary-100 mb-10">
              Join thousands of users who have simplified their lives with Dashit.io.
              Create your free account today—no credit card required.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <button
                onClick={() => navigate('/register')}
                className="px-10 py-4 bg-white text-primary-500 rounded-xl hover:shadow-2xl transition font-bold text-lg flex items-center gap-2 group"
              >
                Sign Up Free
                <ChevronRight className="w-5 h-5 group-hover:translate-x-1 transition" />
              </button>
              <button
                onClick={() => navigate('/login')}
                className="px-10 py-4 bg-primary-600 text-white rounded-xl hover:bg-primary-700 transition font-semibold text-lg border border-primary-400"
              >
                Sign In
              </button>
            </div>
            <p className="text-primary-100 mt-6 text-sm">
              ✓ Free forever &nbsp;•&nbsp; ✓ No credit card required
            </p>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-gray-300 py-12">
        <div className="container mx-auto px-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8 mb-8">
            <div>
              <div className="flex items-center mb-4">
                <img
                  src={logoSrc}
                  alt="Dashit"
                  className="h-8 w-auto"
                  onError={(e) => {
                    e.currentTarget.style.display = 'none';
                    const text = document.createElement('span');
                    text.textContent = 'Dashit';
                    text.className = 'text-xl font-bold text-white';
                    e.currentTarget.parentElement?.appendChild(text);
                  }}
                />
              </div>
              <p className="text-gray-400 text-sm">
                The ultimate dashboard for organizing your personal life, family, and work.
              </p>
            </div>
            
            <div>
              <h4 className="font-semibold text-white mb-4">Product</h4>
              <ul className="space-y-2 text-sm">
                <li><a href="#features" className="hover:text-white transition">Features</a></li>
                <li><a href="#pricing" className="hover:text-white transition">Pricing</a></li>
                <li><a href="#widgets" className="hover:text-white transition">Widgets</a></li>
                <li><a href="#" className="hover:text-white transition">API Docs</a></li>
              </ul>
            </div>
            
            <div>
              <h4 className="font-semibold text-white mb-4">Company</h4>
              <ul className="space-y-2 text-sm">
                <li><a href="#" className="hover:text-white transition">About</a></li>
                <li><a href="#" className="hover:text-white transition">Blog</a></li>
                <li><a href="#" className="hover:text-white transition">Careers</a></li>
                <li><a href="#" className="hover:text-white transition">Contact</a></li>
              </ul>
            </div>
            
            <div>
              <h4 className="font-semibold text-white mb-4">Legal</h4>
              <ul className="space-y-2 text-sm">
                <li><a href="#" className="hover:text-white transition">Privacy Policy</a></li>
                <li><a href="#" className="hover:text-white transition">Terms of Service</a></li>
                <li><a href="#" className="hover:text-white transition">Cookie Policy</a></li>
                <li><a href="#" className="hover:text-white transition">Security</a></li>
              </ul>
            </div>
          </div>
          
          <div className="border-t border-gray-800 pt-8 flex flex-col md:flex-row items-center justify-between gap-4">
            <p className="text-gray-400 text-sm">
              © 2025 Dashit.io. All rights reserved.
            </p>
            <div className="flex items-center gap-6">
              <a href="#" className="hover:text-white transition">
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M24 4.557c-.883.392-1.832.656-2.828.775 1.017-.609 1.798-1.574 2.165-2.724-.951.564-2.005.974-3.127 1.195-.897-.957-2.178-1.555-3.594-1.555-3.179 0-5.515 2.966-4.797 6.045-4.091-.205-7.719-2.165-10.148-5.144-1.29 2.213-.669 5.108 1.523 6.574-.806-.026-1.566-.247-2.229-.616-.054 2.281 1.581 4.415 3.949 4.89-.693.188-1.452.232-2.224.084.626 1.956 2.444 3.379 4.6 3.419-2.07 1.623-4.678 2.348-7.29 2.04 2.179 1.397 4.768 2.212 7.548 2.212 9.142 0 14.307-7.721 13.995-14.646.962-.695 1.797-1.562 2.457-2.549z"/></svg>
              </a>
              <a href="#" className="hover:text-white transition">
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M12 0c-6.626 0-12 5.373-12 12 0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23.957-.266 1.983-.399 3.003-.404 1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576 4.765-1.589 8.199-6.086 8.199-11.386 0-6.627-5.373-12-12-12z"/></svg>
              </a>
              <a href="#" className="hover:text-white transition">
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z"/></svg>
              </a>
            </div>
          </div>
        </div>
      </footer>

      {/* Custom CSS for grid pattern */}
      <style>{`
        .bg-grid-pattern {
          background-image: 
            linear-gradient(to right, rgba(0,0,0,0.1) 1px, transparent 1px),
            linear-gradient(to bottom, rgba(0,0,0,0.1) 1px, transparent 1px);
          background-size: 40px 40px;
        }
      `}</style>
    </div>
  )
}

function Plus(props: any) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      {...props}
    >
      <line x1="12" y1="5" x2="12" y2="19"></line>
      <line x1="5" y1="12" x2="19" y2="12"></line>
    </svg>
  )
}
